import React from "react";
import { FiBookOpen } from "react-icons/fi";
import {
  Badge,
  Code,
  CodeBlock,
  EnvTable,
  Note,
  Section,
  Table,
  Tbody,
  Thead,
  MONO,
  TD,
} from "./primitives";

const PRICING_PAYLOAD = `{
  "date_range": { "start_date": "yesterday" },
  "filters": { "asset_id": { "operator": "EQ", "value": 391 } },
  "metric_group": "ASSET_PRICE_DETAIL",
  "time_granularity": "hour",
  "interval_alignment": "ending"
}`;

const GROUPED_PAYLOAD = `{
  "date_range": { "start_date": "2025-09-30", "end_date": "2025-10-01" },
  "filters": { "asset_id": { "operator": "IN", "value": [391, 392] } },
  "time_granularity": "hour",
  "metrics": ["RUC_MW"],
  "group_by": "market_type"
}`;

const REVENUE_PAYLOAD = `{
  "date_range": { "start_date": "minus_7d" },
  "filters": { "asset_id": { "operator": "EQ", "value": 391 } },
  "time_granularity": "day",
  "group_by": "product",
  "include_yesterday_revenue": true
}`;

const PRICE_NODE_PAYLOAD = `{
  "iso_code": "ERCOT",
  "date_range": { "start_date": "yesterday" }
}`;

const PRICE_NODE_FALLBACK_SHAPE = `{
  "PRICE_NODE_NAME": "HUB_NORTH",
  "ISO_CODE": "ERCOT",
  "AS_OF_DATE": "2026-09-07",
  "DA_LMP_PEAK": 44.38,
  "DA_LMP_OFF_PEAK": 42.42,
  "RT_LMP_PEAK": 51.0,
  "RT_LMP_OFF_PEAK": 47.25,
  "RT_LMP_SOURCE_METRIC": "RT_LMP_H",
  "RT_LMP_SOURCE_GRANULARITY": "hour"
}`;

const RESPONSE_SHAPE = `{
  "status": "Success",
  "code": 200,
  "request": { "...": "echoes back the normalized request" },
  "data": [ { "...": "assets/resources/metrics, shape depends on group_by" } ],
  "meta": {
    "request_id": "uuid",
    "timestamp": "2026-03-17T10:00:00.000000",
    "execution_time": "123.45ms",
    "record_count": 42,
    "pagination": {}
  },
  "error": null
}`;

const ERROR_SHAPE = `{
  "meta": { "request_id": "...", "timestamp": "...", "total_rows": null, "pagination": null },
  "request": { "method": "POST", "path": "...", "params": null },
  "response": null,
  "error": {
    "status": "error",
    "code": 500,
    "error": { "message": "..." }
  }
}`;

const FULL_EXAMPLE = `import requests

tenant_id = "09bb5b3b-dfce-473f-9615-47efe8193e80"

# --- Environment ---------------------------------------------------------
user_env = "stage"  # change to "prod" for production
if user_env == "stage":
    client_id = "your_stage_client_id"
    client_secret = "your_stage_client_secret"
    subscription_key = "your_stage_assetanalytics_subscription_key"
    scope = "api://stage-api-betm-com/.default"
    base_url = "https://api-stg.betm.com/"
else:
    client_id = "your_prod_client_id"
    client_secret = "your_prod_client_secret"
    subscription_key = "your_prod_assetanalytics_subscription_key"
    scope = "api://api-betm-com/.default"
    base_url = "https://api.betm.com/"

# --- Step 1: get a token -------------------------------------------------
token_url = f"https://login.microsoftonline.com/{tenant_id}/oauth2/v2.0/token"
token_response = requests.post(
    token_url,
    data={
        "grant_type": "client_credentials",
        "client_id": client_id,
        "client_secret": client_secret,
        "scope": scope,
    },
)
token_response.raise_for_status()
access_token = token_response.json()["access_token"]

# --- Step 2: call the service -------------------------------------------
headers = {
    "Authorization": f"Bearer {access_token}",
    "Content-Type": "application/json",
    "Product": "EXT",
    "Ocp-Apim-Subscription-Key": subscription_key,
}
payload = {
    "date_range": {"start_date": "today"},
    "filters": {"asset_id": {"operator": "EQ", "value": 391}},
    "metric_group": "ASSET_PRICE_DETAIL",
    "time_granularity": "hour",
    "interval_alignment": "ending",
}
response = requests.post(f"{base_url}timeseries/pricing/", headers=headers, json=payload)
response.raise_for_status()

result = response.json()
print(f"Record count: {result['meta']['record_count']}")
print(result["data"])`;

const AnalyticsDoc: React.FC<{ onOpenMetricDictionary: () => void }> = ({
  onOpenMetricDictionary,
}) => (
  <div className="space-y-8">
    <p className="text-sm leading-6 text-gray-700">
      Market analytics for entitled assets: pricing, volume and revenue metrics and timeseries. The
      service is published under the <Code>timeseries</Code> path prefix and uses the shared
      authentication described under Authentication &amp; Environments.
    </p>

    <Section title="Environment">
      <EnvTable
        prodUrl="https://api.betm.com/timeseries"
        stageUrl="https://api-stg.betm.com/timeseries"
        product="AssetAnalytics_Product"
      />
    </Section>

    <Section title="Integration endpoints">
      <p className="text-sm leading-6 text-gray-700">
        The supported surface for external and partner integrations. All take an{" "}
        <Code>AnalyticsRequest</Code> body unless noted.
      </p>
      <Table minWidth={820}>
        <Thead cols={["Method", "Path", "Description", "Availability"]} />
        <Tbody>
          <tr>
            <td className={TD}>POST</td>
            <td className={`${TD} font-mono`}>/pricing/</td>
            <td className={TD}>
              Pricing metrics and timeseries for assets. Supports{" "}
              <Code>interval_alignment</Code> and enriches results with{" "}
              <Code>peak_price_node_by_asset</Code>.
            </td>
            <td className={TD}>
              <Badge tone="prod">Prod &amp; Stage</Badge>
            </td>
          </tr>
          <tr>
            <td className={TD}>POST</td>
            <td className={`${TD} font-mono`}>/pricing/price-node</td>
            <td className={TD}>
              Peak/off-peak pricing compared across price nodes in an ISO, or an explicit set of
              price node IDs, independent of asset. Body: <Code>PriceNodePricingRequest</Code>.
            </td>
            <td className={TD}>
              <Badge tone="prod">Prod &amp; Stage</Badge>
            </td>
          </tr>
          <tr>
            <td className={TD}>POST</td>
            <td className={`${TD} font-mono`}>/volume/</td>
            <td className={TD}>
              Volume metrics and timeseries. Supports <Code>interval_alignment</Code>.
            </td>
            <td className={TD}>
              <Badge tone="prod">Prod &amp; Stage</Badge>
            </td>
          </tr>
          <tr>
            <td className={TD}>POST</td>
            <td className={`${TD} font-mono`}>/volume/delta</td>
            <td className={TD}>
              Volume delta metrics. Supports <Code>interval_alignment</Code>.
            </td>
            <td className={TD}>
              <Badge tone="prod">Prod &amp; Stage</Badge>
            </td>
          </tr>
          <tr>
            <td className={TD}>POST</td>
            <td className={`${TD} font-mono`}>/volume/total-cycle</td>
            <td className={TD}>Total cycle volume metrics.</td>
            <td className={TD}>
              <Badge tone="prod">Prod &amp; Stage</Badge>
            </td>
          </tr>
          <tr>
            <td className={TD}>POST</td>
            <td className={`${TD} font-mono`}>/revenue/</td>
            <td className={TD}>
              Revenue metrics; <Code>category</Code> is forced to <Code>REVENUE</Code> server-side.
              Supports <Code>interval_alignment</Code>, <Code>group_by</Code> and{" "}
              <Code>group_by_granularity</Code>, and adds a <Code>sort_order</Code> field to the
              response.
            </td>
            <td className={TD}>
              <Badge tone="prod">Prod &amp; Stage</Badge>
            </td>
          </tr>
          <tr>
            <td className={TD}>POST</td>
            <td className={`${TD} font-mono`}>/revenue/peak-breakdown</td>
            <td className={TD}>Revenue broken down by peak and off-peak.</td>
            <td className={TD}>
              <Badge tone="prod">Prod &amp; Stage</Badge>
            </td>
          </tr>
          <tr>
            <td className={TD}>POST</td>
            <td className={`${TD} font-mono`}>/metrics/</td>
            <td className={TD}>Combined energy metrics across categories.</td>
            <td className={TD}>
              <Badge tone="prod">Prod &amp; Stage</Badge>
            </td>
          </tr>
          <tr>
            <td className={TD}>POST</td>
            <td className={`${TD} font-mono`}>/metrics/portfolio-landing</td>
            <td className={TD}>Portfolio landing aggregation used by the AMP dashboards.</td>
            <td className={TD}>
              <Badge tone="prod">Prod &amp; Stage</Badge>
            </td>
          </tr>
        </Tbody>
      </Table>
    </Section>

    <Section title="Other endpoints on the gateway">
      <p className="text-sm leading-6 text-gray-700">
        Reachable through the gateway but not part of the integration contract. They back the AMP
        screens and admin tooling, and their shapes may change without notice.
      </p>
      <Table minWidth={760}>
        <Thead cols={["Area", "Endpoints", "Availability"]} />
        <Tbody>
          <tr>
            <td className={`${TD} font-medium`}>PI timeseries</td>
            <td className={`${TD} ${MONO}`}>
              POST /pi/historical, /pi/current, /pi/notification, /pi/attributes, /pi/end_value; GET
              /pi/status
            </td>
            <td className={TD}>
              <Badge tone="prod">Prod &amp; Stage</Badge>
            </td>
          </tr>
          <tr>
            <td className={`${TD} font-medium`}>Metric configuration</td>
            <td className={`${TD} ${MONO}`}>
              GET/POST/PUT/DELETE /metric-config, /metric-group, /metric-resource/pi-metric-resource;
              GET /metric-config/metadata
            </td>
            <td className={TD}>
              <Badge tone="prod">Prod &amp; Stage</Badge>
            </td>
          </tr>
          <tr>
            <td className={`${TD} font-medium`}>Reference data &amp; cache</td>
            <td className={`${TD} ${MONO}`}>
              POST /reference-data/metric-metadata, /reference-data/sunlight,
              /reference-data/clear-legacy, /cache/clear, /cache/delete
            </td>
            <td className={TD}>
              <Badge tone="prod">Prod &amp; Stage</Badge>
            </td>
          </tr>
        </Tbody>
      </Table>
    </Section>

    <Section title="Request payload">
      <p className="text-sm leading-6 text-gray-700">
        <Code>AnalyticsRequest</Code> is the shared body for <Code>/pricing</Code>,{" "}
        <Code>/volume</Code>, <Code>/volume/delta</Code>, <Code>/revenue</Code>,{" "}
        <Code>/revenue/peak-breakdown</Code> and <Code>/metrics</Code>.
      </p>
      <Table minWidth={780}>
        <Thead cols={["Field", "Required", "Notes"]} />
        <Tbody>
          <tr>
            <td className={`${TD} ${MONO}`}>date_range</td>
            <td className={TD}>Yes</td>
            <td className={TD}>
              <Code>start_date</Code> and <Code>end_date</Code> in <Code>YYYY-MM-DD</Code>, or a
              keyword in <Code>start_date</Code> only: <Code>day_ahead</Code>, <Code>today</Code>,{" "}
              <Code>yesterday</Code>, <Code>minus_7d</Code>, <Code>minus_30d</Code>, <Code>7d</Code>,{" "}
              <Code>mtd</Code>, <Code>ytd</Code>.
            </td>
          </tr>
          <tr>
            <td className={`${TD} ${MONO}`}>filters</td>
            <td className={TD}>
              Yes on <Code>/pricing</Code>, <Code>/volume</Code>, <Code>/revenue</Code>
            </td>
            <td className={TD}>
              These endpoints read per-asset data, so an unscoped request would fan out across every
              asset. Operators: <Code>EQ</Code>, <Code>NE</Code>, <Code>IN</Code>,{" "}
              <Code>NOT_IN</Code>, <Code>GT</Code>, <Code>LT</Code>, <Code>GE</Code>,{" "}
              <Code>LE</Code>. Common fields: <Code>asset_id</Code>, <Code>market_type</Code>,{" "}
              <Code>iso</Code>, <Code>product</Code>. An empty <Code>{"{}"}</Code> is accepted and
              means &quot;every entitled asset&quot;.
            </td>
          </tr>
          <tr>
            <td className={`${TD} ${MONO}`}>metrics / metric_group</td>
            <td className={TD}>
              Yes on <Code>/revenue</Code>
            </td>
            <td className={TD}>
              Validated against the live metric mapping; invalid values return <Code>422</Code>. The
              revenue endpoints require at least one of the two.
            </td>
          </tr>
          <tr>
            <td className={`${TD} ${MONO}`}>time_granularity</td>
            <td className={TD}>No</td>
            <td className={TD}>
              <Code>5minute</Code>, <Code>15minute</Code>, <Code>hour</Code>, <Code>day</Code>,{" "}
              <Code>week</Code>, <Code>month</Code>.
            </td>
          </tr>
          <tr>
            <td className={`${TD} ${MONO}`}>interval_alignment</td>
            <td className={TD}>No</td>
            <td className={TD}>
              <Code>beginning</Code> or <Code>ending</Code>. When omitted, each value keeps its
              asset&apos;s native per-ISO direction, which can differ across granularities: ERCOT
              5-minute data is interval-beginning while 15-minute and hourly are interval-ending. Set
              it to force one uniform convention. No effect on <Code>day</Code>/<Code>week</Code>/
              <Code>month</Code>.
            </td>
          </tr>
          <tr>
            <td className={`${TD} ${MONO}`}>group_by</td>
            <td className={TD}>No</td>
            <td className={TD}>
              <Code>market_type</Code>, <Code>award_type</Code>, <Code>product</Code>,{" "}
              <Code>iso</Code>, <Code>settlement_charge_name</Code>, <Code>resource</Code>,{" "}
              <Code>total</Code>. Pairs with <Code>group_by_metrics</Code> and{" "}
              <Code>group_by_granularity</Code>.
            </td>
          </tr>
          <tr>
            <td className={`${TD} ${MONO}`}>category / operation</td>
            <td className={TD}>No</td>
            <td className={TD}>
              Free-form audit strings, not validated enums. <Code>category</Code> is set
              automatically per endpoint and the revenue endpoints override whatever you send.
            </td>
          </tr>
          <tr>
            <td className={`${TD} ${MONO}`}>include_yesterday_revenue</td>
            <td className={TD}>No</td>
            <td className={TD}>
              Boolean, default <Code>false</Code>.
            </td>
          </tr>
          <tr>
            <td className={`${TD} ${MONO}`}>user</td>
            <td className={TD}>No</td>
            <td className={TD}>
              Accepted for logging and traceability only. Your identity and entitlements are resolved
              from the access token, never from this field, so setting it grants nothing.
            </td>
          </tr>
        </Tbody>
      </Table>
    </Section>

    <Section title="Metrics and metric groups">
      <p className="text-sm leading-6 text-gray-700">
        The valid values for <Code>metrics</Code> and <Code>metric_group</Code> are configuration,
        not code, so they change as new metrics are onboarded. The Metric Dictionary tab in this
        Knowledge Center is the live list: it shows every metric with its ISO, category, market type,
        units, granularity and the groups it belongs to. Common groups are{" "}
        <Code>ASSET_PRICE_DETAIL</Code>, <Code>ASSET_VOLUME_DETAIL</Code> and{" "}
        <Code>ASSET_SETTLEMENT_DETAIL</Code>.
      </p>
      <button
        type="button"
        onClick={onOpenMetricDictionary}
        className="inline-flex items-center gap-2 rounded-md bg-red-50 px-4 py-2 text-sm font-medium text-red-700 ring-1 ring-inset ring-red-200 hover:bg-red-100"
      >
        <FiBookOpen />
        Open the Metric Dictionary
      </button>
    </Section>

    <Section title="Example payloads">
      <Note tone="info">
        The <Code>asset_id</Code> <Code>391</Code> below is illustrative. Requesting an asset you are
        not entitled to returns <Code>403</Code>:{" "}
        <Code>User does not have access to any of the requested assets</Code>. Substitute one of your
        own entitled asset IDs.
      </Note>
      <CodeBlock code={PRICING_PAYLOAD} label="Pricing, with a uniform interval convention" />
      <CodeBlock code={GROUPED_PAYLOAD} label="Volume, grouped by market type" />
      <CodeBlock code={REVENUE_PAYLOAD} label="Revenue, grouped by product" />
      <CodeBlock code={PRICE_NODE_PAYLOAD} label="Price node comparison" />
      <Note>
        On <Code>/pricing/price-node</Code>, a null real-time price is a legitimate response, not
        an outage. Each price node is mapped to specific metrics, and a node can be absent from the
        five-minute source view that backs <Code>RT_LMP</Code> while still having a real-time price
        at a coarser granularity — ERCOT&apos;s <Code>HUB_NORTH</Code> is the standing example.
        Where a coarser mapped metric has data, the response fills{" "}
        <Code>RT_LMP_PEAK</Code>/<Code>RT_LMP_OFF_PEAK</Code> from it and declares the substitution
        in two extra fields, so you can label the difference rather than treat hourly data as
        five-minute:
        <CodeBlock code={PRICE_NODE_FALLBACK_SHAPE} label="Real-time price served from a coarser metric" />
        Treat <Code>{"{metric}_SOURCE_METRIC"}</Code> and{" "}
        <Code>{"{metric}_SOURCE_GRANULARITY"}</Code> as optional: they are present only when a
        substitution actually happened, and absent on every normal response. Peak and off-peak
        values are always the mean over the <em>last day</em> of the requested range, so a
        substituted value still covers the same target day as the rest of the row.
      </Note>
    </Section>

    <Section title="Response">
      <p className="text-sm leading-6 text-gray-700">
        Successful responses share one envelope. On failure <Code>status</Code> is{" "}
        <Code>&quot;Error&quot;</Code>, <Code>data</Code> is empty and <Code>error</Code> is
        populated.
      </p>
      <CodeBlock code={RESPONSE_SHAPE} label="Success envelope" />
      <Note>
        Not every failure uses that envelope. A framework-level error, such as a{" "}
        <Code>500</Code> from a missing required field on <Code>/revenue/</Code>, returns a different
        shape with no top-level <Code>status</Code>, <Code>code</Code> or <Code>data</Code>. Handle
        both forms.
      </Note>
      <CodeBlock code={ERROR_SHAPE} label="Framework-level error shape" />
    </Section>

    <Section title="Worked example">
      <CodeBlock code={FULL_EXAMPLE} label="Python - authenticate and request pricing metrics" />
    </Section>
  </div>
);

export default AnalyticsDoc;
